<template>
  <div>
    <vue-headful :title="this.setPageTitle('monitoringHistory')" />
    <loading :active="isLoading" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- TITLE -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <span class="mr-2">
            <a
              @click.prevent="
                $router.push({
                  name: 'monitoringList',
                })
              "
              class="pointer"
            >
              <span class="mr-2 align-middle"><i class="fas fa-chevron-left fa-lg"></i></span
            ></a>
            <span class="mb-0 h4 font-weight-normal align-middle">{{ $t('monitoringHistory.title') }}</span>
          </span>
        </template>
      </custom-flex-header>

      <!-- Filters -->
      <b-row class="mb-4">
        <b-col cols="4">
          <label class="fieldLabel" for="productName" v-t="'monitoringHistory.productLabel'" />
          <custom-multi-select
            :placeholder="$t('common.search')"
            :options="productNameSelectOptions"
            v-model="productNameSelectValue"
          ></custom-multi-select>
        </b-col>
        <b-col cols="4" v-if="isMOP">
          <label class="fieldLabel" for="investmentOption" v-t="'monitoringHistory.investmentOptionLabel'" />
          <custom-multi-select
            :placeholder="$t('common.search')"
            :options="investmentOptionSelectOptions"
            v-model="investmentOptionSelectValue"
          ></custom-multi-select>
        </b-col>
        <b-col cols="4" v-if="isDriverIndicatorsSelectEnabled">
          <label class="fieldLabel" for="driver" v-t="getDriversIndicatorsSelectLabel" />
          <custom-multi-select
            :placeholder="$t('common.search')"
            :options="formattedDriversOptions"
            v-model="driverSelectValue"
          ></custom-multi-select>
        </b-col>
      </b-row>

      <!-- Profile association -->
      <b-row class="mb-4" v-if="profileName">
        <b-col>
          <div>
            <custom-tooltip
              :tooltipText="$t('monitoringHistory.tooltipText')"
              :section="'monitoring-history'"
            ></custom-tooltip>
            {{ $t('monitoringHistory.associatedToProfile') }}
            <span class="font-weight-bold">{{ profileName }}</span>
          </div>
        </b-col>
      </b-row>

      <!-- Chart -->
      <template v-if="chartVisibilityConditions">
        <b-row class="mb-4">
          <b-col>
            <line-chart
              :chart-key="hcKey"
              :chart-data="chartData"
              :chart-config="hcChartConfig"
              :chart-series-name="chartSeriesName"
              :chart-language-callback="setOptions"
              :chart-title="$t('monitoringHistory.chartTitle')"
              :chart-formatter-callback="chartFormatterCallback"
            ></line-chart>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <no-content :icon-name="'exclamation-circle'" :icon-class="['mb-3', 'fa-3x']" :text="noContentReason">
        </no-content>
      </template>
    </b-container>
  </div>
</template>

<script>
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import CustomMultiSelect from '../../components/Lib/CustomMultiSelect.vue'
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import LineChart from '../../components/ScoringProfiles/LineChart.vue'
import NoContent from '../../components/Lib/NoContent.vue'
import { INIT_MONITORING_HISTORY_FILTERS } from '../../settings/scoringProfilesSettings'
import { setChartMenuItemsLang } from '../../utils/formatter'

export default {
  components: {
    CustomMultiSelect,
    CustomTooltip,
    CustomFlexHeader,
    LineChart,
    NoContent,
  },
  data() {
    return {
      filters: INIT_MONITORING_HISTORY_FILTERS(),
      chartData: [],
      profileName: null,
      productOptions: [],
      investmentOptions: [],
      driverOptions: [],
      associatedModel: false,
      isLoading: false,
      noContentReason: null,
      hcKey: 0,
      hcChartConfig: {
        chart: {
          type: 'line',
          zoomType: 'xy',
          margin: [],
          backgroundColor: '#FFFFFF',
        },
        xAxis: {
          type: 'datetime',
          style: {
            fontSize: '0.75em',
          },
          rotation: -45,
        },
        yAxis: {
          min: 0,
          max: 1,
          tickInterval: 0.1,
          formatter: (val) => `${val.value * 100}%`,
        },
        series: {
          color: '#9EEA85',
          marker: {
            enabled: true,
          },
        },
        marker: {
          symbol: 'square',
          lineWidth: 2,
          lineColor: '',
        },
      },
    }
  },
  computed: {
    isMOP() {
      return this.investmentOptionSelectOptions.length > 0
    },

    isDriverIndicatorsSelectEnabled() {
      return this.driverSelectOptions.length > 0
    },

    hasAssociatedModel: {
      get() {
        return this.associatedModel
      },
      set(newVal) {
        this.associatedModel = newVal
      },
    },

    chartVisibilityConditions: {
      get() {
        const isDataLoaded = !this.isLoading && this.chartData.length > 0
        const isProductSelected = this.productNameSelectValue.length > 0
        const isInvestmentOptionSelected = this.investmentOptionSelectValue.length > 0
        const isDriverIndicatorSelected = this.driverSelectValue.length > 0

        return this.isMOP
          ? isProductSelected && isInvestmentOptionSelected && isDriverIndicatorSelected && isDataLoaded
          : isProductSelected && isDriverIndicatorSelected && isDataLoaded
      },
    },

    productNameSelectValue: {
      get() {
        return this.filters.productName ? this.filters.productName : []
      },
      set(newVal) {
        this.filters.productName = newVal
        if (newVal[0]) {
          this.onChangeProduct(newVal[0])
        }
      },
    },

    chartSeriesName: {
      get() {
        let name = ''
        const indicator = this.driverSelectValue[0].toUpperCase()
        const drivers = this.$i18n.messages[this.$i18n.locale].drivers
        if (indicator in drivers.categories) {
          name = this.$t('drivers.categories.' + indicator)
        } else if (indicator in drivers.codes) {
          name = this.$t('drivers.codes.' + indicator)
        } else if (indicator in drivers.custom) {
          name = this.$t('drivers.custom.' + indicator)
        }
        return name
      },
    },

    productNameSelectOptions: {
      get() {
        return this.productOptions
      },
      set(newVal) {
        this.productOptions = newVal
      },
    },

    investmentOptionSelectValue: {
      get() {
        return this.filters.investmentOption ? this.filters.investmentOption : []
      },
      set(newVal) {
        this.filters.investmentOption = newVal
        this.onChangeInvestmentOptionDriver()
      },
    },

    investmentOptionSelectOptions: {
      get() {
        return this.investmentOptions
      },
      set(newVal) {
        this.investmentOptions = newVal
      },
    },

    driverSelectValue: {
      get() {
        return this.filters.driver ? this.filters.driver : []
      },
      set(newVal) {
        this.filters.driver = newVal
        this.onChangeInvestmentOptionDriver()
      },
    },

    driverSelectOptions: {
      get() {
        return this.driverOptions
      },
      set(newVal) {
        this.driverOptions = newVal
      },
    },

    formattedDriversOptions: {
      get() {
        const drivers = [...this.driverSelectOptions].map((j) => {
          return {
            value: j,
            text: this.hasAssociatedModel
              ? this.$t('drivers.categories.' + j.toUpperCase())
              : this.$t('drivers.codes.' + j.toUpperCase()),
          }
        })
        return [
          ...drivers,
          {
            value: 'MARKETBASEDVFM',
            text: this.$t('drivers.custom.MARKETBASEDVFM'),
          },
        ]
      },
    },

    getDriversIndicatorsSelectLabel() {
      return this.hasAssociatedModel ? 'monitoringHistory.driverLabel' : 'monitoringHistory.indicatorsLabel'
    },
  },
  methods: {
    onChangeProduct(id) {
      this.profileName = null
      this.investmentOptionSelectValue = null
      this.driverSelectValue = null
      this.driverSelectOptions = []
      this.investmentOptionSelectOptions = []

      if (this.isAssociatedToScoringProfile(id)) {
        this.retrieveProductDetails(parseInt(id))
        this.noContentReason = this.$t('common.noContent')
      } else {
        this.noContentReason = this.$t('monitoringHistory.noProfileAssociated')
      }
    },

    isAssociatedToScoringProfile(productId) {
      const product = this.productNameSelectOptions.find((el) => el.value === productId)
      return product && product.scoringProfileId
    },

    onChangeInvestmentOptionDriver() {
      const isInvestmentOptionSelected = this.isMOP ? this.investmentOptionSelectValue[0] : true
      const isDriverSelected = this.driverSelectValue.length > 0

      if (isInvestmentOptionSelected && isDriverSelected) {
        const productId = parseInt(this.productNameSelectValue[0])
        const investmentOptionId = this.investmentOptionSelectOptions
          ? parseInt(this.investmentOptionSelectValue[0])
          : null
        const driverOrIndicatorCode = this.driverSelectValue[0]

        this.retrieveChartData(productId, investmentOptionId, driverOrIndicatorCode)
      }
    },

    retrieveProducts() {
      this.isLoading = true

      const payload = {
        sf: null,
        companyProductTypes: [1],
        scoringProfileIds: null,
        sort: 'name',
        page: 1,
        size: 10000,
      }

      this.post('/api/company/productsSearch', payload)
        .then((data) => {
          let products = []

          if (data.products) {
            data.products.forEach((el) => {
              products.push({
                value: el.id.toString(),
                text: el.name,
                scoringProfileId: el.scoringProfileId,
              })
            })
          }

          this.productNameSelectOptions = products
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    retrieveProductDetails(id) {
      this.isLoading = true

      const payload = {
        productId: id,
      }

      this.post('/api/scoringMonitoring/productDetail', payload)
        .then((data) => {
          let investmentOptions = []

          if (data.investmenOptions) {
            data.investmenOptions.forEach((el) => {
              investmentOptions.push({ value: el.id.toString(), text: el.name })
            })
          }

          this.investmentOptionSelectOptions = investmentOptions
          this.profileName = data.scoringProfile ? data.scoringProfile.name : null
          this.hasAssociatedModel = data.scoringProfile ? data.scoringProfile.hasModel : false

          if (Array.isArray(data.driverOrIndicatorCodes) && data.driverOrIndicatorCodes.length > 0) {
            this.driverSelectOptions = data.driverOrIndicatorCodes
          }
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    setOptions() {
      this.hcKey += 1
      return setChartMenuItemsLang()
    },

    chartFormatterCallback(obj, symbol) {
      let result = []
      Object.keys(obj).forEach((k) => result.push(this.formatter(obj[k].val, obj[k].format)))
      return result.length > 1 ? result.join(symbol) : result[0]
    },

    formatter(val, format) {
      let out
      switch (format) {
        case 'percent':
          out = this.$n(val, 'percent')
          break
        case 'date':
          out = this.$d(new Date(val), 'short')
          break
        default:
          out = val
      }
      return out
    },

    retrieveChartData(productId, investmentOptionId, driverOrIndicatorCode) {
      this.isLoading = true

      const payload = {
        productId: productId,
        investmenteOptionId: investmentOptionId,
        driverOrIndicatorCode: driverOrIndicatorCode,
      }

      this.post('/api/scoringMonitoring/history', payload)
        .then((data) => {
          const values =
            'monitoredValues' in data && Array.isArray(data.monitoredValues) && data.monitoredValues.length > 0
              ? data.monitoredValues
              : []

          this.chartData = this.generateChartData(values) || []
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    generateChartData(data) {
      let points = []

      let sortedData = data.sort(
        (a, b) => new Date(a.date.substring(0, 10)).getTime() - new Date(b.date.substring(0, 10)).getTime()
      )

      sortedData.forEach((el) => {
        const x = new Date(el.date.split('T')[0]).getTime()
        const y = !isNaN(el.score) ? parseFloat(el.score) : null
        points.push([x, y])
      })

      return points
    },
  },
  created() {
    this.noContentReason = this.$t('common.noContent')
    this.retrieveProducts()
  },
}
</script>

<style></style>
